import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { DraftDocumentEditor } from '../components/DraftDocumentEditor.js';
import { formatMoney, marginPercent, toCents } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';
import { AttachmentSection } from '../components/Attachments.js';
import { useCan } from '../lib/session.js';

/**
 * One invoice.
 *
 * Laid out the way the document itself reads — who it is for, what was done, what it comes
 * to — because that is the shape people already know from the paper version, and an invoice
 * screen that does not match the invoice is hard to check against a customer on the phone.
 *
 * Two things are surfaced that the old system had the data for and never showed: whether the
 * customer ever opened it, and the margin. Margin is gated on `billing.cost.read`; the API
 * strips the cost fields entirely for anyone else, so there is nothing to leak here.
 */

type TaxLine = { code: string; rate: string; taxableAmount: string; amount: string; registrationNumber: string | null };

type InvoiceItem = {
  id: string;
  name: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  unitCost?: string;
  unitOfMeasure: string | null;
  taxable: boolean;
  subtotal: string;
  total: string;
};

type Invoice = {
  id: string;
  number: string | null;
  status: string;
  issuedOn: string | null;
  dueOn: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  subtotal: string;
  taxTotal: string;
  total: string;
  paidTotal: string;
  balance: string;
  costTotal?: string;
  taxes: TaxLine[];
  note: string | null;
  sentAt: string | null;
  firstViewedAt: string | null;
  billingSnapshot: {
    billedTo: string;
    organization: string | null;
    address: { line1: string; line2: string | null; city: string; region: string; postalCode: string | null } | null;
  } | null;
  customer: { id: string; name: string };
  job: { id: string | null; number: string } | null;
  items: InvoiceItem[];
  payments: Array<{ id: string; number: string | null; method: string; receivedAt: string; appliedAmount: string }>;
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon',
  awaiting_payment: 'En attente de paiement',
  partial: 'Partiellement payée',
  paid: 'Payée',
  voided: 'Annulée',
};

const METHOD_LABEL: Record<string, string> = {
  credit_card: 'Carte de crédit',
  e_transfer: 'Virement Interac',
  check: 'Chèque',
  cash: 'Comptant',
  interac: 'Interac',
  ach: 'Prélèvement',
  other: 'Autre',
};

export function InvoiceDetailScreen() {
  const { invoiceId } = useParams({ from: '/invoices/$invoiceId' });
  const can = useCan();

  const query = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => request<Invoice>(`/invoices/${invoiceId}`),
  });

  if (query.isLoading) return <p className="screen__loading">Chargement…</p>;
  if (query.isError || !query.data) {
    return (
      <div className="screen">
        <div className="notice notice--error">Cette facture est introuvable.</div>
      </div>
    );
  }

  const invoice = query.data;
  const owed = toCents(invoice.balance);
  const overdue = owed > 0 && invoice.dueOn && new Date(invoice.dueOn) < new Date() && invoice.status !== 'draft';
  const showCost = can('billing.cost.read') && invoice.costTotal !== undefined;
  /*
   * A zero cost total is almost always "nobody entered a cost", not "this cost us nothing" —
   * cost prices are blank throughout the imported history. Reporting 100% margin from that
   * would be a confident lie, so the two cases are told apart and the unknown one says so.
   */
  const margin =
    showCost && toCents(invoice.costTotal!) > 0
      ? marginPercent(toCents(invoice.total), toCents(invoice.costTotal!))
      : null;

  return (
    <div className="screen screen--customer">
      <RecordLink kind="customer" id={invoice.customer.id} className="screen__breadcrumb">
        ← {invoice.customer.name}
      </RecordLink>

      <header className="doc__head">
        <div className="doc__identity">
          <span className="label">Facture</span>
          <h1 className="doc__number tnum">{invoice.number ?? 'Sans numéro'}</h1>
          <span className={`chip doc__status doc__status--${invoice.status}`}>{STATUS_LABEL[invoice.status] ?? invoice.status}</span>
        </div>

        {/* Billed-to comes from the frozen snapshot, not the live customer: it is what the
            document said when it was issued, and an invoice must not silently rewrite itself
            when somebody corrects a spelling. */}
        <address className="doc__billed">
          <span className="label">Facturé à</span>
          <span className="doc__billedName">{invoice.billingSnapshot?.billedTo ?? invoice.customer.name}</span>
          {invoice.billingSnapshot?.organization && <span>{invoice.billingSnapshot.organization}</span>}
          {invoice.billingSnapshot?.address && (
            <>
              <span>{invoice.billingSnapshot.address.line1}</span>
              {invoice.billingSnapshot.address.line2 && <span>{invoice.billingSnapshot.address.line2}</span>}
              <span>
                {invoice.billingSnapshot.address.city}, {invoice.billingSnapshot.address.region}{' '}
                <span className="tnum">{invoice.billingSnapshot.address.postalCode}</span>
              </span>
            </>
          )}
        </address>

        <dl className="doc__meta">
          <div>
            <dt className="label">Émise</dt>
            <dd>{invoice.issuedOn ? new Date(invoice.issuedOn).toLocaleDateString('fr-CA') : '—'}</dd>
          </div>
          <div>
            <dt className="label">Échéance</dt>
            <dd className={overdue ? 'doc__overdue' : undefined}>
              {invoice.dueOn ? new Date(invoice.dueOn).toLocaleDateString('fr-CA') : '—'}
            </dd>
          </div>
          {invoice.job && (
            <div>
              <dt className="label">Travail</dt>
              <dd>
                {invoice.job.id ? (
                  <RecordLink kind="job" id={invoice.job.id} className="cell__link">
                    {invoice.job.number}
                  </RecordLink>
                ) : (
                  invoice.job.number
                )}
              </dd>
            </div>
          )}
          <div>
            <dt className="label">Envoyée</dt>
            {/* Whether they ever opened it decides whether chasing is even warranted. */}
            <dd>
              {invoice.sentAt
                ? invoice.firstViewedAt
                  ? `Ouverte le ${new Date(invoice.firstViewedAt).toLocaleDateString('fr-CA')}`
                  : 'Jamais ouverte'
                : 'Pas envoyée'}
            </dd>
          </div>
        </dl>
      </header>

      <table className="table doc__items">
        <caption>{invoice.items.length} article(s)</caption>
        <thead>
          <tr>
            <th scope="col">Description</th>
            <th scope="col" style={{ textAlign: 'right' }}>Qté</th>
            <th scope="col" style={{ textAlign: 'right' }}>Prix</th>
            <th scope="col" style={{ textAlign: 'right' }}>Montant</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((item) => (
            <tr key={item.id}>
              <td className="cell">
                <span className="doc__itemName">{item.name}</span>
                {/* These descriptions are long — a full service procedure — so they are
                    clamped and expandable rather than allowed to bury the totals. */}
                {item.description && (
                  <details className="doc__itemDetails">
                    <summary>Détails</summary>
                    <p>{item.description}</p>
                  </details>
                )}
              </td>
              <td className="cell numeric">{item.quantity}{item.unitOfMeasure ? ` ${item.unitOfMeasure}` : ''}</td>
              <td className="cell numeric money">{formatMoney(toCents(item.unitPrice), 'fr')}</td>
              <td className="cell numeric money">{formatMoney(toCents(item.subtotal), 'fr')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="doc__totals" aria-label="Totaux">
        <div className="doc__totalRow">
          <span>Sous-total</span>
          <span className="money">{formatMoney(toCents(invoice.subtotal), 'fr')}</span>
        </div>
        {invoice.taxes.map((tax) => (
          <div key={tax.code} className="doc__totalRow doc__totalRow--tax">
            <span>
              {tax.code} <span className="tnum">{tax.rate}%</span>
              {tax.registrationNumber && <span className="doc__taxNumber tnum">{tax.registrationNumber}</span>}
            </span>
            <span className="money">{formatMoney(toCents(tax.amount), 'fr')}</span>
          </div>
        ))}
        <div className="doc__totalRow doc__totalRow--grand">
          <span>Total</span>
          <span className="money">{formatMoney(toCents(invoice.total), 'fr')}</span>
        </div>
        {toCents(invoice.paidTotal) > 0 && (
          <div className="doc__totalRow">
            <span>Payé</span>
            <span className="money money--settled">−{formatMoney(toCents(invoice.paidTotal), 'fr')}</span>
          </div>
        )}
        <div className="doc__totalRow doc__totalRow--balance">
          <span>Solde</span>
          <span className={owed > 0 ? 'money money--due' : 'money money--settled'}>
            {formatMoney(owed, 'fr')}
          </span>
        </div>

        {/* Only for those who may see cost. The API omits the fields for everyone else, so
            there is nothing here to hide badly. */}
        {showCost && (
          <div className="doc__totalRow doc__totalRow--margin">
            <span>Marge</span>
            {margin === null ? (
              <span className="doc__unknown">Coût non renseigné</span>
            ) : (
              <span className="tnum">
                {margin.toFixed(1)} % · {formatMoney(toCents(invoice.total) - toCents(invoice.costTotal!), 'fr')}
              </span>
            )}
          </div>
        )}
      </section>

      {invoice.payments.length > 0 && (
        <section className="doc__section">
          <h2 className="section__title">Paiements appliqués</h2>
          <ul className="doc__payments">
            {invoice.payments.map((payment) => (
              <li key={payment.id}>
                <span>{new Date(payment.receivedAt).toLocaleDateString('fr-CA')}</span>
                <span>{METHOD_LABEL[payment.method] ?? payment.method}</span>
                <span className="money">{formatMoney(toCents(payment.appliedAmount), 'fr')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {invoice.note && (
        <section className="doc__section">
          <h2 className="section__title">Note sur la facture</h2>
          {/* Plain text: the stored note is HTML, and rendering markup out of the database is
              not a risk worth taking for some bold tags. */}
          <p className="doc__note">{invoice.note}</p>
        </section>
      )}

      {/* Lines can still be changed while this is a draft. Once issued, it is void-and-reissue. */}
      <section className="doc__section">
        <h2 className="section__title">Modifier le brouillon</h2>
        <DraftDocumentEditor
          kind="invoice"
          id={invoice.id}
          status={invoice.status}
          note={invoice.note}
          date={invoice.dueOn}
          items={invoice.items}
          locale="fr"
        />
      </section>

      {/*
        The files on the work this invoice bills — the before-and-after photographs, the
        inspection report, the manufacturer's sheet for the unit that was replaced. An
        attachment belongs to a job rather than to a document, so this resolves through the
        job; an invoice with no job shows nothing, and shows nothing quietly.
      */}
      <AttachmentSection scope={{ invoiceId }} />
    </div>
  );
}
